import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Linkedin, Instagram, Youtube, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, FOOTER_NAV, FOOTER_LEGAL, FOOTER_SOCIAL, CONTACT_EMAIL } from "../../lib/content";
import { track } from "../../lib/analytics";
import { useSite } from "../../context/site";
import { ArrowField, PyramidBuild } from "./motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Static-first, high-contrast system: near-black #0A0A0A, pure white, lime accent. */

export const Eyebrow = ({ children, dark, className = "" }) => (
  <p
    className={`font-body text-sm font-semibold uppercase tracking-[0.18em] text-[#5f8f10] ${className}`}
  >
    {children}
  </p>
);

/* Sharp CTA, instant hover invert, no motion/shadow tricks */
export const Cta = ({ children, primary, dark, to, onClick, testId, className = "" }) => {
  const base =
    "font-body inline-flex items-center gap-3 border-2 px-8 py-4 text-sm font-semibold uppercase tracking-[0.14em] transition-colors duration-150 ";
  let variant;
  if (primary) variant = "border-[#5f8f10] bg-[#5f8f10] text-[#161A15] hover:border-[#161A15] hover:bg-[#161A15] hover:text-white";
  else if (dark) variant = "border-white bg-transparent text-white hover:bg-white hover:text-[#161A15]";
  else variant = "border-[#161A15] bg-transparent text-[#161A15] hover:bg-[#161A15] hover:text-white";
  const content = (
    <>
      {children}
      <span aria-hidden>→</span>
    </>
  );
  if (to) {
    return (
      <Link to={to} data-testid={testId} onClick={onClick} className={base + variant + " " + className}>
        {content}
      </Link>
    );
  }
  return (
    <button data-testid={testId} onClick={onClick} className={base + variant + " " + className}>
      {content}
    </button>
  );
};

export const MarkTile = ({ src, alt = "", className = "" }) => (
  <img
    src={src}
    alt={alt}
    aria-hidden={alt ? undefined : true}
    loading="lazy"
    decoding="async"
    className={`mx-auto block h-auto w-full max-w-[380px] object-contain md:mr-0 md:ml-auto md:max-w-[420px] ${className}`}
  />
);

/* Full-bleed cinematic breaker: scattered effort aligning into one green trajectory.
   Used between folds to relieve dense text with the brand motif. */
export const TrajectoryBand = ({ flip = false, label, testId, src = "/assets/editorial-trajectory.jpg" }) => (
  <div data-testid={testId} className="relative h-[150px] w-full overflow-hidden bg-[#161A15] md:h-[210px]">
    <img
      src={src}
      alt=""
      aria-hidden
      loading="lazy"
      decoding="async"
      className={`band-drift absolute inset-0 h-full w-full object-cover opacity-90 ${flip ? "-scale-x-100" : ""}`}
    />
    <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(22,26,21,0.55) 0%, rgba(22,26,21,0) 42%, rgba(22,26,21,0.55) 100%)" }} />
    {label && (
      <div className="absolute inset-0 flex items-center px-6 md:px-12 lg:px-16">
        <p className="font-body text-xs font-semibold uppercase tracking-[0.22em] text-white/80 md:text-sm">
          {label}
        </p>
      </div>
    )}
  </div>
);

/* Shared charcoal page hero with the aligned arrow-field motion. */
export const PageHero = ({ eyebrow, title, subcopy, crumbs, testId }) => (
  <section data-testid={testId} className="relative overflow-hidden bg-[#161A15] pb-20 pt-36 text-white md:pb-28 md:pt-44">
    <div aria-hidden className="pointer-events-none absolute inset-y-0 right-0 hidden w-[44%] lg:block">
      <ArrowField active className="h-full w-full" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,#161A15 0%, rgba(22,26,21,0.55) 26%, rgba(22,26,21,0) 62%)" }} />
    </div>
    <div className="relative z-10 mx-auto max-w-[1360px] px-6 md:px-12 lg:px-16">
      {crumbs && crumbs.length > 0 && (
        <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="mb-6">
          <ol className="flex flex-wrap items-center gap-2 font-body text-xs font-semibold uppercase tracking-[0.14em] text-white/50">
            {crumbs.map((c, i) => (
              <li key={c.label} className="flex items-center gap-2">
                {i > 0 && <span aria-hidden className="text-white/30">/</span>}
                {c.href ? <Link to={c.href} className="transition-colors hover:text-[#5f8f10]">{c.label}</Link> : <span className="text-[#5f8f10]">{c.label}</span>}
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="max-w-3xl">
        <Eyebrow dark>{eyebrow}</Eyebrow>
        <h1 className="font-display mt-6 text-5xl leading-[0.92] tracking-tight md:text-7xl lg:text-8xl">{title}</h1>
        {subcopy && <p className="font-body mt-8 max-w-2xl text-xl font-normal leading-relaxed text-white/80">{subcopy}</p>}
      </div>
    </div>
  </section>
);

/* Shared "What we believe" section (Home + About) so they never drift apart. */
export const BelieveBlock = ({ testId = "believe-block" }) => (
  <section data-testid={testId} className="border-b border-black/10 bg-[#F7F6F2]">
    <div className="mx-auto grid max-w-[1360px] items-center gap-12 px-6 py-24 md:grid-cols-12 md:px-12 md:py-32 lg:gap-16 lg:px-16">
      <div className="md:col-span-6">
        <Eyebrow>What we believe</Eyebrow>
        <h2 className="font-display mt-6 text-4xl leading-[0.95] tracking-tight text-black md:text-5xl">
          Growth is a<br />system, <span className="text-[#5f8f10]">not a<br />slogan.</span>
        </h2>
        <p className="font-body mt-8 max-w-lg text-lg font-normal leading-relaxed text-black/80">
          When every function pulls in its own direction, effort stops compounding. We reorganise the business around one growth problem and one path to execution, from separate efforts to one growth agenda.
        </p>
      </div>
      <div className="md:col-span-6">
        <PyramidBuild className="max-w-[520px]" />
      </div>
    </div>
  </section>
);

/* Shared charcoal closing CTA band. */
export const CtaBand = ({ heading, onClick, to, label = "Bring Us Your Growth Problem", testId }) => (
  <section className="border-t-2 border-[#5f8f10] bg-[#161A15]">
    <div className="mx-auto grid max-w-[1360px] items-center gap-10 px-6 py-24 md:grid-cols-12 md:px-12 md:py-28 lg:px-16">
      <h2 className="font-display md:col-span-8 text-4xl leading-[0.95] tracking-tight text-white md:text-5xl lg:text-6xl">{heading}</h2>
      <div className="md:col-span-4 md:flex md:justify-end">
        <Cta primary onClick={onClick} to={to} testId={testId}>{label}</Cta>
      </div>
    </div>
  </section>
);


export const SiteHeader = () => {
  const [menu, setMenu] = useState(false);
  const { openTalk } = useSite();
  return (
    <>
      <header data-testid="site-header" className="fixed inset-x-0 top-0 z-[80] border-b border-[#161A15]/10 bg-[#F7F6F2]/90 backdrop-blur-md">
        <div className="mx-auto flex max-w-[1360px] items-center justify-between px-6 py-4 md:px-12 lg:px-16">
          <Link to="/" data-testid="header-logo-link" aria-label="SWITCH, home" className="flex shrink-0 items-center">
            <img src="/assets/wordmark.png" alt="SWITCH" className="h-[1.8rem] w-auto md:h-[2.1rem]" />
          </Link>
          <nav className="hidden items-center gap-7 lg:flex">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`header-nav-${l.href}`}
                onClick={() => track("header_nav_clicked", { label: l.label })}
                className="font-body whitespace-nowrap text-xs font-semibold uppercase tracking-[0.12em] text-[#161A15] transition-colors duration-150 hover:text-[#5f8f10]"
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="flex shrink-0 items-center gap-4">
            <div className="hidden items-center gap-3.5 lg:flex">
              {FOOTER_SOCIAL.map((l) => {
                const Icon = SOCIAL_ICON[Object.keys(SOCIAL_ICON).find((k) => l.label.toLowerCase().includes(k))];
                return (
                  <a key={l.label} href={l.href} target="_blank" rel="noreferrer" aria-label={l.label} data-testid={`header-social-${l.label.toLowerCase()}`} className="text-[#161A15]/55 transition-colors hover:text-[#5f8f10]">
                    {Icon && <Icon size={18} strokeWidth={1.9} />}
                  </a>
                );
              })}
            </div>
            <button
              aria-label="Open menu"
              data-testid="header-menu-button"
              onClick={() => setMenu(true)}
              className="flex h-11 w-11 items-center justify-center text-[#161A15] transition-colors hover:text-[#5f8f10] lg:hidden"
            >
              <Menu size={26} strokeWidth={1.75} />
            </button>
          </div>
        </div>
      </header>

      {menu && (
        <div data-testid="mobile-menu" className="fixed inset-0 z-[90] lg:hidden">
          {/* scrim keeps the site visible behind the panel */}
          <button
            aria-label="Close menu"
            data-testid="mobile-menu-scrim"
            onClick={() => setMenu(false)}
            className="panel-scrim absolute inset-0 h-full w-full cursor-default bg-[#161A15]/55 backdrop-blur-[3px]"
          />
          <aside className="panel-slide absolute right-0 top-0 flex h-full w-[86%] max-w-[380px] flex-col bg-[#161A15] shadow-[-24px_0_60px_rgba(0,0,0,0.35)]">
            <div className="flex items-center justify-between border-b border-white/12 px-6 py-5">
              <img src="/assets/wordmark.png" alt="SWITCH" className="h-6 w-auto brightness-0 invert" />
              <button aria-label="Close menu" data-testid="mobile-menu-close" onClick={() => setMenu(false)} className="flex h-10 w-10 items-center justify-center text-white/70 transition-colors hover:text-[#5f8f10]">
                <X size={22} strokeWidth={1.75} />
              </button>
            </div>

            <nav className="flex flex-1 flex-col justify-center overflow-y-auto px-6 py-6">
              {MENU_ITEMS.map((l, i) => (
                <Link
                  key={l.href}
                  to={l.href}
                  data-testid={`mobile-menu-link-${l.href}`}
                  onClick={() => { track("mobile_nav_clicked", { label: l.label }); setMenu(false); }}
                  style={{ animationDelay: `${0.1 + i * 0.05}s` }}
                  className="panel-item group flex items-center justify-between border-b border-white/10 py-3.5"
                >
                  <span className="flex items-baseline gap-3">
                    <span className="font-body text-[0.6rem] font-semibold tracking-[0.2em] text-[#5f8f10]">{String(i + 1).padStart(2, "0")}</span>
                    <span className="font-display text-xl uppercase tracking-tight text-white transition-colors duration-150 group-hover:text-[#5f8f10]">{l.label}</span>
                  </span>
                  <ArrowUpRight size={18} strokeWidth={1.75} className="text-white/30 transition-all duration-150 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#5f8f10]" />
                </Link>
              ))}
            </nav>

            <div className="border-t border-white/12 px-6 py-5">
              <Cta primary onClick={() => { setMenu(false); openTalk(); }} testId="mobile-menu-cta" className="w-full justify-between !py-3.5">
                Talk to Us
              </Cta>
              <div className="mt-5 flex items-center gap-5">
                {FOOTER_SOCIAL.map((l) => {
                  const Icon = SOCIAL_ICON[Object.keys(SOCIAL_ICON).find((k) => l.label.toLowerCase().includes(k))];
                  return (
                    <a key={l.label} href={l.href} target="_blank" rel="noreferrer" aria-label={l.label} className="text-white/50 transition-colors hover:text-[#5f8f10]">
                      {Icon && <Icon size={19} strokeWidth={1.75} />}
                    </a>
                  );
                })}
              </div>
            </div>
          </aside>
        </div>
      )}
    </>
  );
};

const SOCIAL_ICON = { linkedin: Linkedin, instagram: Instagram, youtube: Youtube };
const MENU_ITEMS = [
  ...NAV_LINKS,
  { label: "Find Your Switch", href: "/find-your-switch" },
  { label: "Contact", href: "/contact" },
  { label: "Careers", href: "/careers" },
];

export const SiteFooter = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [message, setMessage] = useState("You're subscribed.");
  const year = new Date().getFullYear();

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    track("newsletter_subscribed");
    try {
      const res = await fetch(`${API}/newsletter`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
      const data = await res.json();
      setMessage(data.status === "already_subscribed" ? "This email is already subscribed." : "You're subscribed.");
    } catch (err) {
      setMessage("You're subscribed.");
    }
    setDone(true);
  };

  const Col = ({ label, links }) => (
    <div>
      <Eyebrow dark>{label}</Eyebrow>
      <ul className="mt-6 space-y-3">
        {links.map((l) => (
          <li key={l.label}>
            <Link to={l.href} className="font-body text-base font-normal text-white/80 transition-colors duration-150 hover:text-[#5f8f10]">
              {l.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden border-t border-white/15 bg-[#0A0C08] text-white">
      <div className="mx-auto max-w-[1360px] px-6 pt-24 md:px-12 lg:px-16">
        <div className="grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <h2 className="font-display text-4xl leading-[0.95] tracking-tight text-white md:text-5xl lg:text-6xl">
              Growth,<br />switched <span className="text-[#5f8f10]">on.</span>
            </h2>

            <form onSubmit={subscribe} className="mt-10 max-w-sm">
              <label htmlFor="nl-email" className="font-body block text-base font-normal text-white/75">
                Subscribe to our newsletter.
              </label>
              {done ? (
                <p data-testid="newsletter-confirm" className="font-body mt-4 text-base font-semibold uppercase tracking-[0.12em] text-[#5f8f10]">
                  {message}
                </p>
              ) : (
                <div className="mt-4 flex">
                  <input
                    id="nl-email"
                    type="email"
                    required
                    data-testid="newsletter-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Your email address"
                    className="font-body w-full border border-white/25 bg-transparent px-4 py-3 text-base font-normal text-white outline-none transition-colors placeholder:text-white/45 focus:border-[#5f8f10]"
                  />
                  <button type="submit" data-testid="newsletter-submit" className="font-body shrink-0 border border-[#5f8f10] bg-[#5f8f10] px-5 py-3 text-sm font-semibold uppercase tracking-[0.12em] text-[#161A15] transition-colors hover:bg-[#4a700c] hover:border-[#4a700c]">
                    Subscribe
                  </button>
                </div>
              )}
            </form>
          </div>

          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-10 sm:grid-cols-3">
              <Col label="Navigate" links={FOOTER_NAV} />
              <Col label="Legal" links={FOOTER_LEGAL} />
              {FOOTER_SOCIAL.length > 0 && (
                <div>
                  <Eyebrow dark>Social</Eyebrow>
                  <ul className="mt-6 space-y-3">
                    {FOOTER_SOCIAL.map((l) => (
                      <li key={l.label}>
                        <a href={l.href} target="_blank" rel="noreferrer" className="font-body inline-flex items-center text-base font-normal text-white/75 transition-colors duration-150 hover:text-[#5f8f10]">
                          {l.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-white/12 py-6">
          <p className="font-body text-sm font-normal uppercase tracking-[0.12em] text-white/55">© {year} SWITCH. All rights reserved.</p>
          <p className="font-body text-sm font-normal uppercase tracking-[0.12em] text-white/45">{CONTACT_EMAIL}</p>
        </div>
      </div>

      {/* Massive wordmark, finality and strength */}
      <div aria-hidden className="relative select-none overflow-hidden px-6 pb-10 md:px-12 lg:px-16">
        <img src="/assets/wordmark.png" alt="" className="mx-auto w-full max-w-[1180px] opacity-[0.10] brightness-0 invert" />
      </div>
    </footer>
  );
};
